import { useMemo } from 'react'
import type { FareClassWithReward, FlightsClassTypes } from 'constants/flight'
import {
  expandMaldivesCharterFareClassesWithRewards,
  getCharterSelectableCashCabins,
} from 'lib/maldivesCharter/charterCabins'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'

/**
 * Canonical "what cabins should the Maldives charter fare-class modal show" list.
 *
 * Filters the caller's `baseAllowedFareClasses` down to the selectable cash cabins,
 * including Economy Extra only when the `lux_row` Optimizely flag is on, then expands
 * with reward-seat variants when the `maldivesCharterRewardSeats` Optimizely flag is on.
 *
 * Both offer-page entry points (`CharterFlightFareClassSelect`, `FlashMobileStickyHeaderFilters`)
 * and Maldives Direct entry points (`MaldivesDirectSearchPage`, `MaldivesDirectSearchFilters`)
 * use this so every surface produces the same card set.
 */
export default function useMaldivesCharterModalFareClasses(
  baseAllowedFareClasses: ReadonlyArray<FlightsClassTypes>,
): ReadonlyArray<FareClassWithReward> {
  const isRewardSeatsEnabled = useRewardSeatsEnabled()
  const isLuxRowEnabled = useFeatureFlag(OptimizelyFeatureFlags.luxRow)
  return useMemo(() => {
    const selectableCashCabins = getCharterSelectableCashCabins(isLuxRowEnabled)
    const cashOnly = selectableCashCabins.filter(fc => baseAllowedFareClasses.includes(fc))
    if (!isRewardSeatsEnabled) return cashOnly
    return expandMaldivesCharterFareClassesWithRewards(cashOnly)
  }, [baseAllowedFareClasses, isLuxRowEnabled, isRewardSeatsEnabled])
}
